import axios from "axios";
import { BaseResponseSingle, BaseResponse } from "../../typings";
import { 
  IStoreClosureDateResponse, 
  IStoreClosureDateRequest 
} from "../../typings/interfaces/responses/store-closure-date-response";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { 
  GET_STORE_CLOSURE_DATES, 
  ADD_STORE_CLOSURE_DATE, 
  UPDATE_STORE_CLOSURE_DATE, 
  DELETE_STORE_CLOSURE_DATE 
} from "../../configs";
import { setStoreClosureDates } from "../../util/store-closures";

// The API answers HTTP 200 with status FAILED when a save fails, so check the
// status too or a failed save looks like a success.
const failureOf = (data?: { status?: string; message?: string }) =>
  data?.status === "SUCCESS"
    ? null
    : { message: data?.message || "Store closure date request failed" };

const errorOf = (err: any) => ({
  message: err?.response?.data?.message || err?.message || "Request failed",
});

export const getStoreClosureDates = createAsyncThunk(
  "store-closure-date/getAll",
  async (
    { storeId, merchantId }: { storeId: number; merchantId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get<
        BaseResponse<IStoreClosureDateResponse>
      >(GET_STORE_CLOSURE_DATES(storeId, merchantId));
      const failure = failureOf(response.data);
      if (failure) return rejectWithValue(failure);
      // Ready dates skip these (see util/store-closures)
      setStoreClosureDates(
        (response.data?.body ?? []).filter((d) => !d.isDeleted)
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(errorOf(err));
    }
  }
);

export const addStoreClosureDate = createAsyncThunk(
  "store-closure-date/add",
  async (
    data: IStoreClosureDateRequest,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post<
        BaseResponseSingle<IStoreClosureDateResponse>
      >(ADD_STORE_CLOSURE_DATE, data);
      const failure = failureOf(response.data);
      if (failure) return rejectWithValue(failure);
      return response.data;
    } catch (err) {
      return rejectWithValue(errorOf(err));
    }
  }
);

export const updateStoreClosureDate = createAsyncThunk(
  "store-closure-date/update",
  async (
    { id, data }: { id: number; data: IStoreClosureDateRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.put<
        BaseResponseSingle<IStoreClosureDateResponse>
      >(UPDATE_STORE_CLOSURE_DATE(id), data);
      const failure = failureOf(response.data);
      if (failure) return rejectWithValue(failure);
      return response.data;
    } catch (err) {
      return rejectWithValue(errorOf(err));
    }
  }
);

export const deleteStoreClosureDate = createAsyncThunk(
  "store-closure-date/delete",
  async (
    id: number,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.delete<
        BaseResponseSingle<boolean>
      >(DELETE_STORE_CLOSURE_DATE(id));
      const failure = failureOf(response.data);
      if (failure) return rejectWithValue(failure);
      return response.data;
    } catch (err) {
      return rejectWithValue(errorOf(err));
    }
  }
);

